/**
 * Per-session transcription / SOAP job progress from WebSocket events.
 * Each visit keeps its own status so parallel live/upload jobs cannot clobber each other.
 */

const BUSY_STAGES = new Set([
  "queued",
  "uploading",
  "processing",
  "transcribing",
  "finalizing",
  "generating_soap",
  "generating",
]);

/** @type {Map<string, object>} */
const bySessionId = new Map();

/** @type {Set<(map: Record<string, object>) => void>} */
const listeners = new Set();

function normalizeSessionId(sessionId) {
  if (sessionId == null) return null;
  const sid = String(sessionId).trim();
  if (!sid || sid === "undefined" || sid === "null") return null;
  return sid;
}

function resolveSessionId(data) {
  if (!data || typeof data !== "object") return null;
  const inner = data.data && typeof data.data === "object" ? data.data : null;
  const nested = inner?.data && typeof inner.data === "object" ? inner.data : null;
  return normalizeSessionId(
    data.sessionId ?? inner?.sessionId ?? nested?.sessionId ?? null
  );
}

function cloneMap() {
  const out = {};
  for (const [sid, row] of bySessionId.entries()) {
    out[sid] = { ...row };
  }
  return out;
}

function notify() {
  const snapshot = cloneMap();
  for (const listener of listeners) {
    try {
      listener(snapshot);
    } catch (_) {
      /* ignore */
    }
  }
}

function toPercent(value, stage) {
  const raw = Number(value);
  const n = Number.isFinite(raw) ? raw : 0;
  if (String(stage || "").toLowerCase() === "completed" && n <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function upsert(sessionId, patch = {}) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  const prev = bySessionId.get(sid) || {};
  const stage = patch.stage != null ? String(patch.stage) : prev.stage || "";
  const status = patch.status != null ? String(patch.status) : prev.status || "";
  const next = {
    jobId: patch.jobId ?? prev.jobId ?? null,
    sessionId: sid,
    stage,
    status,
    percent: toPercent(
      patch.percent != null ? patch.percent : prev.percent,
      stage
    ),
    message: patch.message != null ? patch.message : prev.message || "",
    queuePosition:
      patch.queuePosition !== undefined
        ? patch.queuePosition
        : prev.queuePosition ?? null,
    updatedAt: Date.now(),
  };
  const prevUpdated = Number(prev.updatedAt) || 0;
  if (
    patch.jobId &&
    prev.jobId &&
    String(patch.jobId) !== String(prev.jobId) &&
    prevUpdated > next.updatedAt - 50
  ) {
    // Same tick: keep the newer job id from patch (intentional replace).
  }
  if (
    prev.jobId === next.jobId &&
    prev.stage === next.stage &&
    prev.status === next.status &&
    prev.percent === next.percent &&
    prev.message === next.message &&
    prev.queuePosition === next.queuePosition
  ) {
    return;
  }

  bySessionId.set(sid, next);
  notify();
}

export function applyTranscriptionSessionEvent(type, data) {
  const sid = resolveSessionId(data);
  if (!sid) return;

  const stage = data?.stage || data?.status || "";
  const status = data?.status || "";

  if (type === "complete") {
    upsert(sid, {
      jobId: data?.jobId,
      stage: "completed",
      status: "completed",
      percent: 100,
      message: data?.message || "Your SOAP note is ready.",
      queuePosition: null,
    });
    return;
  }

  if (type === "error") {
    upsert(sid, {
      jobId: data?.jobId,
      stage: "failed",
      status: "failed",
      percent: 0,
      message: data?.error || data?.message || "Failed",
      queuePosition: null,
    });
    return;
  }

  upsert(sid, {
    jobId: data?.jobId,
    stage: stage || status || "processing",
    status: status || (stage === "queued" ? "queued" : "processing"),
    percent: data?.percent ?? data?.progress ?? 0,
    message: data?.message || "",
    queuePosition: data?.queuePosition ?? null,
  });
}

export function applyLocalSessionProgress(sessionId, patch = {}) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  const requestedStage = patch.stage != null ? String(patch.stage) : "";
  const stage =
    requestedStage ||
    (patch.active === false ? "completed" : "processing");
  const failed = stage === "failed" || patch.status === "failed";
  upsert(sid, {
    jobId: patch.jobId,
    stage: failed ? "failed" : stage,
    status:
      patch.status ||
      (failed ? "failed" : patch.active === false ? "completed" : "processing"),
    percent:
      failed
        ? patch.percent ?? 0
        : patch.active === false && patch.percent == null
          ? 100
          : patch.percent,
    message: patch.message,
    queuePosition: patch.queuePosition,
  });
}

export function getProgressForSession(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return null;
  return bySessionId.get(sid) || null;
}

export function getProgressBySessionMap() {
  return cloneMap();
}

export function isBusyStage(stage) {
  return BUSY_STAGES.has(String(stage || "").toLowerCase());
}

export function toUiProgress(row) {
  if (!row) {
    return { active: false, stage: "", percent: 0, message: "", jobId: null };
  }
  const stage = row.stage || "";
  return {
    active: isBusyStage(stage),
    stage,
    percent: toPercent(row.percent, stage),
    message: row.message || "",
    jobId: row.jobId || null,
    status: row.status || "",
    sessionId: row.sessionId || null,
  };
}

export function subscribeTranscriptionJobProgress(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  try {
    listener(cloneMap());
  } catch (_) {
    /* ignore */
  }
  return () => listeners.delete(listener);
}
